import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Calendar, Clock, Mail, Plus, Edit, Trash2, Users } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { getAuthHeaders } from "../lib/api";

interface EmailSchedule {
  id: string;
  name: string;
  reportType: string;
  schedule: string;
  recipients: string[];
  active: boolean;
  lastSent?: string;
  createdAt: string;
}

export default function ScheduledReports() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<EmailSchedule | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    reportType: "daily",
    schedule: "0 8 * * *", // Default: 8 AM daily
    recipients: [] as string[],
    active: true
  });
  const [newRecipient, setNewRecipient] = useState("");
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch scheduled reports
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["/api/email/schedules"],
    queryFn: async () => {
      const response = await fetch("/api/email/schedules", {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Failed to fetch schedules");
      return response.json();
    }
  });

  // Fetch email recipients
  const { data: recipients = [] } = useQuery({
    queryKey: ["/api/email/recipients"],
    queryFn: async () => {
      const response = await fetch("/api/email/recipients", {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Failed to fetch recipients");
      return response.json();
    }
  });

  // Create schedule mutation
  const createScheduleMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await fetch("/api/email/schedules", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error("Failed to create schedule");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email/schedules"] });
      toast({
        title: "Schedule Created",
        description: "Automatic report schedule has been created successfully"
      });
      resetForm();
      setIsCreateOpen(false);
    },
    onError: () => {
      toast({
        title: "Creation Failed",
        description: "Failed to create schedule",
        variant: "destructive"
      });
    }
  });

  // Update schedule mutation
  const updateScheduleMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const response = await fetch(`/api/email/schedules/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error("Failed to update schedule");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email/schedules"] });
      toast({
        title: "Schedule Updated",
        description: "Report schedule has been updated successfully"
      });
      resetForm();
      setEditingSchedule(null);
    }
  });

  // Delete schedule mutation
  const deleteScheduleMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/email/schedules/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error("Failed to delete schedule");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email/schedules"] });
      toast({
        title: "Schedule Deleted",
        description: "Report schedule has been deleted"
      });
    }
  });

  const resetForm = () => {
    setFormData({
      name: "",
      reportType: "daily",
      schedule: "0 8 * * *",
      recipients: [],
      active: true
    });
    setNewRecipient("");
  };

  const addRecipient = () => {
    if (newRecipient && newRecipient.includes("@") && !formData.recipients.includes(newRecipient)) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, newRecipient]
      }));
      setNewRecipient("");
    }
  };

  const removeRecipient = (email: string) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const handleSubmit = () => {
    if (editingSchedule) {
      updateScheduleMutation.mutate({ id: editingSchedule.id, data: formData });
    } else {
      createScheduleMutation.mutate(formData);
    }
  };

  const startEdit = (schedule: EmailSchedule) => {
    setFormData({
      name: schedule.name,
      reportType: schedule.reportType,
      schedule: schedule.schedule,
      recipients: schedule.recipients,
      active: schedule.active
    });
    setEditingSchedule(schedule);
  };

  const getScheduleDescription = (schedule: string) => {
    const parts = schedule.split(" ");
    if (parts[1] === "8" && parts[2] === "*" && parts[3] === "*" && parts[4] === "*") {
      return "Daily at 8:00 AM";
    }
    if (parts[1] === "8" && parts[2] === "*" && parts[3] === "*" && parts[4] === "1") {
      return "Weekly on Monday at 8:00 AM";
    }
    if (parts[1] === "8" && parts[2] === "1" && parts[3] === "*" && parts[4] === "*") {
      return "Monthly on 1st at 8:00 AM";
    }
    return schedule;
  };

  return (
    <div className="p-6 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Scheduled Reports</h1>
              <p className="text-muted-foreground mt-2">Automate weighbridge reports for daily, weekly, and monthly delivery</p>
            </div>
        
        <Dialog open={isCreateOpen || !!editingSchedule} onOpenChange={(open) => {
          if (!open) {
            setIsCreateOpen(false);
            setEditingSchedule(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={() => setIsCreateOpen(true)} data-testid="button-create-schedule">
              <Plus className="w-4 h-4 mr-2" />
              Create Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingSchedule ? "Edit Report Schedule" : "Create Report Schedule"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Schedule Name */}
              <div className="space-y-2">
                <Label>Schedule Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Daily Operations Report"
                  data-testid="input-schedule-name"
                />
              </div>

              {/* Report Type */}
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select value={formData.reportType} onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, reportType: value }));
                  // Auto-update schedule based on type
                  if (value === "daily") {
                    setFormData(prev => ({ ...prev, schedule: "0 8 * * *" }));
                  } else if (value === "weekly") {
                    setFormData(prev => ({ ...prev, schedule: "0 8 * * 1" }));
                  } else if (value === "monthly") {
                    setFormData(prev => ({ ...prev, schedule: "0 8 1 * *" }));
                  }
                }}>
                  <SelectTrigger data-testid="select-report-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily Report</SelectItem>
                    <SelectItem value="weekly">Weekly Report</SelectItem>
                    <SelectItem value="monthly">Monthly Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule Time */}
              <div className="space-y-2">
                <Label>Schedule (Cron Format)</Label>
                <Input
                  value={formData.schedule}
                  onChange={(e) => setFormData(prev => ({ ...prev, schedule: e.target.value }))}
                  placeholder="0 8 * * *"
                  data-testid="input-schedule-cron"
                />
                <p className="text-sm text-gray-500">
                  Current: {getScheduleDescription(formData.schedule)}
                </p>
              </div>

              {/* Recipients */}
              <div className="space-y-2">
                <Label>Email Recipients</Label>
                <div className="flex space-x-2">
                  <Input
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    placeholder="Enter email address"
                    onKeyPress={(e) => e.key === "Enter" && addRecipient()}
                    data-testid="input-new-recipient"
                  />
                  <Button onClick={addRecipient} variant="outline" size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Display added recipients */}
                {formData.recipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.recipients.map((email) => (
                      <Badge key={email} variant="secondary" className="flex items-center gap-1">
                        {email}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => removeRecipient(email)}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Quick add from existing recipients */}
                {recipients.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm">Quick Add from Contacts:</Label>
                    <div className="flex flex-wrap gap-2">
                      {recipients.map((recipient: any) => (
                        <Button
                          key={recipient.id}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!formData.recipients.includes(recipient.email)) {
                              setFormData(prev => ({
                                ...prev,
                                recipients: [...prev.recipients, recipient.email]
                              }));
                            }
                          }}
                          disabled={formData.recipients.includes(recipient.email)}
                        >
                          {recipient.name} ({recipient.email})
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Active Switch */}
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                  data-testid="switch-active"
                />
                <Label>Active Schedule</Label>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setIsCreateOpen(false);
                  setEditingSchedule(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={createScheduleMutation.isPending || updateScheduleMutation.isPending || !formData.name || formData.recipients.length === 0}
                  data-testid="button-save-schedule"
                >
                  {createScheduleMutation.isPending || updateScheduleMutation.isPending ? (
                    "Saving..."
                  ) : (
                    editingSchedule ? "Update Schedule" : "Create Schedule"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Schedules Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Active Schedules
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading schedules...</div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No scheduled reports configured</p>
              <p className="text-sm">Create your first automated report schedule</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Sent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map((schedule: EmailSchedule) => (
                  <TableRow key={schedule.id}>
                    <TableCell className="font-medium">{schedule.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {schedule.reportType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {getScheduleDescription(schedule.schedule)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {schedule.recipients.length} recipient{schedule.recipients.length !== 1 ? 's' : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={schedule.active ? "default" : "secondary"}>
                        {schedule.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {schedule.lastSent ? 
                        new Date(schedule.lastSent).toLocaleDateString() : 
                        "Never"
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(schedule)}
                          data-testid={`button-edit-${schedule.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteScheduleMutation.mutate(schedule.id)}
                          data-testid={`button-delete-${schedule.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}